import { useEffect, useState } from 'react'
import { ArrowLeft } from 'lucide-react'
import { ScrollArea } from '@/components/ui/scroll-area'
import { SurgerySourceRow } from '@/components/evidence/SurgerySourceRow'
import { InlineDiff } from '@/components/evidence/InlineDiff'
import { statusBadgeConfig } from '@/components/evidence/statusConfig'
import type { AtomicBreakdownEntry, Evidence } from '@/types/api'
import type { ShareSnapshotSource } from '@/types/share'

interface ShareSurgeryPanelProps {
  entry: AtomicBreakdownEntry
  /** Lookup keyed by source UUID — used to render the citation header
   *  row for each visible evidence. */
  sourceById: Map<string, ShareSnapshotSource>
  onBack: () => void
}

function isComplexDecomposition(atomicClaim: string, originalSentence: string): boolean {
  const claimWords = atomicClaim.toLowerCase().split(/\s+/)
  const originalWords = originalSentence.toLowerCase().split(/\s+/)
  if (originalWords.length > 0 && claimWords.length / originalWords.length < 0.6) return true
  const originalSet = new Set(originalWords)
  const overlap = claimWords.filter(w => originalSet.has(w)).length
  if (claimWords.length > 0 && overlap / claimWords.length < 0.6) return true
  return false
}

function shareCitationLabel(src: ShareSnapshotSource | undefined, fallback: string): string {
  if (!src) return fallback
  return src.short_citation || src.ama_citation || src.filename || fallback
}

/**
 * Read-only Surgery panel for the public share view. Mirrors the in-app
 * ``SurgeryPanel`` (see ``components/evidence/SurgeryPanel.tsx``) but
 * strips every interactive control:
 *   - No Hide source / Hide passage / Remove citation actions.
 *   - No PDF viewer modal (the "View in PDF" buttons stay visible but
 *     greyed with a "Not available in shared view" tooltip).
 *   - No audit trail (per-session history that doesn't survive a
 *     snapshot anyway).
 * The visual chrome — claim header, status badge, scrutiny flags,
 * sources list, suggested-fix block, AI analysis — matches the app via
 * the shared ``SurgerySourceRow`` primitive.
 */
export function ShareSurgeryPanel({ entry, sourceById, onBack }: ShareSurgeryPanelProps) {
  const [expandedSourceIndex, setExpandedSourceIndex] = useState<number | null>(null)

  // Reset expansion when the active claim swaps.
  useEffect(() => {
    /* eslint-disable-next-line react-hooks/set-state-in-effect -- intentional reset when parent swaps the surgeried claim */
    setExpandedSourceIndex(null)
  }, [entry])

  const hiddenSourceIds = new Set(entry.review_state?.hidden_source_ids ?? [])
  const hiddenPassageIds = new Set(entry.review_state?.hidden_passage_ids ?? [])

  const rawEvidences = (entry.evidences ?? []).filter(
    (ev): ev is Evidence => !!ev?.source_doc,
  )
  const visibleEvidences = rawEvidences.filter(ev => !hiddenSourceIds.has(ev.source_doc))

  const status = entry.status ?? 'unverified'
  const badge = statusBadgeConfig[status] ?? statusBadgeConfig['unverified']

  return (
    <ScrollArea className="flex-1 min-h-0">
      <div className="p-3 flex flex-col">
        <button
          type="button"
          className="text-[11px] text-primary hover:text-primary/80 cursor-pointer inline-flex items-center gap-1 mb-3 self-start"
          onClick={onBack}
        >
          <ArrowLeft className="h-3 w-3" />
          Back to all claims
        </button>

        <p className="text-[13px] font-medium text-foreground leading-[1.4] mb-2">
          {entry.claim}
        </p>

        <div className="flex items-center gap-1.5 flex-wrap mb-3.5">
          <span className={`text-[10px] font-medium px-2 py-0.5 rounded-[10px] ${badge.className}`}>
            {badge.label}
          </span>
          {entry.scrutiny_flags?.map(flag => (
            <span
              key={flag.type}
              className="text-[10px] px-2 py-0.5 rounded-[10px] border border-border text-muted-foreground"
            >
              {flag.type.charAt(0).toUpperCase() + flag.type.slice(1)} claim
            </span>
          ))}
        </div>

        <div className="mb-3.5">
          <div className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider mb-1.5">
            Sources ({visibleEvidences.length})
          </div>
          {visibleEvidences.map((ev, i) => (
            <SurgerySourceRow
              key={ev.source_doc + i}
              evidence={ev}
              sourceLabel={shareCitationLabel(sourceById.get(ev.source_doc), ev.source_doc)}
              isExpanded={expandedSourceIndex === i}
              hiddenPassageIds={hiddenPassageIds}
              onToggle={() => setExpandedSourceIndex(expandedSourceIndex === i ? null : i)}
              onViewSource={() => { /* read-only — disabled in SurgerySourceRow */ }}
              onRemoveSource={() => { /* read-only */ }}
              onViewPassage={() => { /* read-only */ }}
              onRemovePassage={() => { /* read-only */ }}
              readOnly
            />
          ))}
          {visibleEvidences.length === 0 && (
            <p className="text-[11px] text-muted-foreground">No evidence available for this claim.</p>
          )}
        </div>

        <div className="h-px bg-border/40 my-3" />

        <div className="mb-3.5">
          <div className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider mb-1.5">
            Suggested fix (in context)
          </div>
          {entry.suggested_edit ? (
            entry.original_sentence && isComplexDecomposition(entry.claim, entry.original_sentence) ? (
              <>
                <p className="text-[11px] text-amber-600 italic mb-1.5">
                  This claim spans a complex sentence. The suggested fix applies to the decomposed claim — review the original text directly.
                </p>
                <div className="bg-muted rounded-lg p-2 px-2.5 text-[11px] leading-relaxed text-foreground">
                  {entry.suggested_edit}
                </div>
              </>
            ) : (
              <InlineDiff originalText={entry.claim} suggestedText={entry.suggested_edit} />
            )
          ) : (
            <p className="text-[11px] text-muted-foreground">No revision suggested for this claim.</p>
          )}
        </div>

        <div className="mb-3.5">
          <div className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider mb-1.5">
            AI analysis
          </div>
          <div className="bg-muted rounded-lg p-2 px-2.5 text-[11px] leading-relaxed text-muted-foreground">
            {entry.reasoning || 'No AI analysis available for this claim.'}
          </div>
        </div>

        <div className="pb-4" />
      </div>
    </ScrollArea>
  )
}
